import {AutoSave, FilterDefinition, FilterOperator} from '@flinkit/ra-form-layout'
import {
  FilterOperatorProps,
} from '@flinkit/ra-form-layout/src/forms/StackedFilters/types'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  ClickAwayListener,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Popper,
  Skeleton,
  Typography,
} from '@mui/material'
import {difference} from 'lodash-es'
import {
  cloneElement,
  FC,
  Fragment,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  CreateButton,
  EditContextProvider,
  List,
  ListProps,
  required,
  SelectInput,
  SimpleForm,
  TopToolbar,
  useChoices,
  useListContext,
  useReference,
  useResourceContext,
  useTranslate,
} from 'react-admin'
import {useFormContext, useWatch} from 'react-hook-form'
import {makeStyles} from 'tss-react/mui'

import DialogCloseButton from '../../components/DialogCloseButton'
import DisplayBox from '../../components/DisplayBox'
import Empty from '../../components/Empty'
import ExtendedDialog from '../../components/ExtendedDialog'
import {AddIcon, CloseIcon, FilterIcon} from '../../components/icons'
import useHasPermission from '../../hooks/useHasPermission'
import useIsDesktop from '../../hooks/useIsDesktop'
import useSelectAllRowIdsInAllPages from '../../hooks/useSelectAllRowIdsInAllPages'
import useTracker from '../../hooks/useTracker'
import ResourceTitle from './ResourceTitle'

const ExtendedList: FC<ExtendedListProps> = ({
  actionButtons,
  advancedFiltersConfig,
  canSelectAllPages = false,
  children,
  description,
  disableSyncWithLocation = true,
  filters,
  hasCreate = false,
  onCreateButtonClick,
  onHasDisplayedEmptyFiltersChange,
  shouldAlignActionButtonsWithFilterForm = false,
  ...props
}) => {
  const {actions, title} = props
  const hasFilters = !!filters
  const translate = useTranslate()
  const r = useResourceContext()
  const resource = props.resource ?? r
  const styles = useStyles({hasFilters}).classes
  return (
    <List
      actions={
        <ExtendedListHeader
          actionButtons={actionButtons}
          description={description}
          hasFilters={hasFilters}
          onCreateButtonClick={onCreateButtonClick}
          shouldAlignActionButtonsWithFilterForm={
            shouldAlignActionButtonsWithFilterForm
          }
          title={title}
        />
      }
      className={styles.root}
      {...props}
      disableSyncWithLocation={disableSyncWithLocation}
      empty={
        <TopToolbar>
          <Box width="100%">
            {actions ?? (
              <ExtendedListHeader
                actionButtons={actionButtons}
                description={description}
                hasFilters={hasFilters}
                onCreateButtonClick={onCreateButtonClick}
                shouldAlignActionButtonsWithFilterForm={
                  shouldAlignActionButtonsWithFilterForm
                }
                title={title}
              />
            )}
            <Empty resource={resource} {...props}/>
          </Box>
        </TopToolbar>
      }
      filters={filters && (
        <FiltersForm
          actionButtons={actionButtons}
          advancedFiltersConfig={advancedFiltersConfig}
          filters={filters}
          onCreateButtonClick={onCreateButtonClick}
          onHasDisplayedEmptyFiltersChange={onHasDisplayedEmptyFiltersChange}
          shouldAlignActionButtonsWithFilterForm={shouldAlignActionButtonsWithFilterForm}
        />
      )}
      title={
        <Box alignItems="center" display="flex" justifyContent="space-between">
          {title ?? translate(`resources.${resource}.name`)}
          <ExtendedListActions
            actionButtons={actionButtons}
            onCreateButtonClick={onCreateButtonClick}
          />
        </Box>
      }
    >
      <WrapperComponent {...{canSelectAllPages, children, hasCreate, resource}} />
    </List>
  )
}

const WrapperComponent = ({canSelectAllPages, children, hasCreate, resource}) => {
  const {isLoading: isPageLoading, total} = useListContext()
  const translate = useTranslate()
  const styles = useStyles({}).classes
  const {
    isAllRowIdsInAllPagesSelected,
    loading: isAllPagesRowIdsLoading,
    selectAllRowIdsInAllPages,
    unselectAllRowIdsInAllPages,
  } = useSelectAllRowIdsInAllPages({isEnabled: canSelectAllPages})
  return (
    <Box width="100%">
      {(canSelectAllPages && !!total) && (
        <FormControlLabel
          className={styles.selectAllPagesField}
          control={
            <Checkbox
              checked={isAllRowIdsInAllPagesSelected}
              color="primary"
              disabled={isAllPagesRowIdsLoading}
              icon={
                isAllPagesRowIdsLoading ? <CircularProgress size="24px" /> : undefined
              }
              onChange={e => e.target.checked ?
                selectAllRowIdsInAllPages() :
                unselectAllRowIdsInAllPages()
              }
            />
          }
          label={translate('extendedList.selectAllPages')}
        />
      )}
      {isPageLoading && (
        <Box display="flex" flexDirection="column" gap="1.5rem" paddingX="2.5rem">
          {[...Array(12).keys()]
            .map(i => <Skeleton height="3.5rem" key={i} variant="rectangular" />)}
        </Box>
      )}
      {!isPageLoading && (
        total ? children : <Empty hasCreate={hasCreate} resource={resource} />
      )}
    </Box>
  )
}

const FiltersForm: FC<FiltersFormProps> = ({
  actionButtons,
  advancedFiltersConfig,
  filters,
  onCreateButtonClick,
  onHasDisplayedEmptyFiltersChange,
  shouldAlignActionButtonsWithFilterForm,
}) => {
  const {
    displayedFilters = {}, filterValues, hideFilter, setFilters, showFilter,
  } = useListContext()
  const styles = useStyles({hasFilters: true}).classes
  const translate = useTranslate()
  const {trackEvent} = useTracker()
  const resource = useResourceContext()
  const inactiveFilters = filters.filter(
    f => !(
      f.props.alwaysOn ||
      Object.prototype.hasOwnProperty.call(displayedFilters, f.props.source)
    )
  )
  const getFieldLabel = useGetFieldLabel()
  const anchorElementRef = useRef<HTMLButtonElement>(null)
  const [isFilterOptionPopperOpen, setIsFilterOptionPopperOpen] = useState(false)
  const [
    isAdvancedFilterOptionPopperOpen, setIsAdvancedFilterOptionPopperOpen,
  ] = useState(false)
  // We use an active filters set so that we can sort the rendered active filters
  // according to the order in which they were selected by the user.
  const [activeFilters, setActiveFilters] = useState(new Set<string>())
  useEffect(() => {
    // We run an effect to find a list of added and removed filters, which
    // can then be used to update the sorted list of active filters.
    // TODO: Run this block code outside of a `useEffect()` to reduce complexity.
    setActiveFilters(active => {
      difference(Object.keys(filterValues), Array.from(active.keys()))
        .forEach(f => active.add(f))
      difference(Array.from(active.keys()), Object.keys(filterValues))
        .forEach(f => active.delete(f))
      return new Set(active)
    })
  }, [filterValues])
  const hasDisplayedEmptyFilters = !!difference(
    Object.keys(displayedFilters), Array.from(activeFilters)
  ).length
  useEffect(() => {
    onHasDisplayedEmptyFiltersChange?.(hasDisplayedEmptyFilters)
  }, [hasDisplayedEmptyFilters, onHasDisplayedEmptyFiltersChange])
  const restoredDisplayedFilters = useAdvancedDisplayedFiltersFromHasuraFilterValues()
  const isDesktop = useIsDesktop()
  const isAdvancedFilteringEnabled = !!advancedFiltersConfig
  const canShowFiltersButton = filters.filter(f => !f.props.alwaysOn).length ||
    advancedFiltersConfig
  const deleteAllAppliedAdvancedFilters = useCallback(() => {
    const values = {...filterValues}
    delete values['_and']
    setFilters(values)
    setIsAdvancedFilterOptionPopperOpen(false)
  }, [filterValues, setFilters])
  return (
    <Box display="flex" flexDirection="row" justifyContent="start" width="100%">
      <EditContextProvider
        // @ts-ignore
        value={{
          mutationMode: 'pessimistic',
          redirect: false,
          // @ts-ignore
          save: values => {
            setFilters({...filterValues, ...values})
            trackEvent({
              action: 'FILL_FILTER',
              category: 'FILTER',
              source: resource?.toUpperCase(),
            })
          },
        }}
      >
        <SimpleForm
          className={styles.filterForm}
          resetOptions={{keepDirtyValues: true}}
          toolbar={(
            <AutoSave
              confirmationDuration={false}
              debounce={0}
              typographyProps={{display: 'none'}}
            />
          )}
        >
          {filters.filter(f => f.props.alwaysOn)}
          {Array.from(activeFilters)
            .reduce<FilterElement[]>((active, source) => {
              const filter = filters.find(f => f.props.source === source)
              return (!filter || filter.props.alwaysOn) ? active : [...active, filter]
            }, [])
            .map((f, i) => (
              // Used to clear a filled filter from the set of active filters.
              <FilledFilterChip
                childProps={f.props.children?.props}
                choices={f.props.choices}
                hideFilter={hideFilter}
                key={i}
                label={f.props.label ?? getFieldLabel(f.props.source)}
                optionText={f.props.optionText}
                optionValue={f.props.optionValue}
                reference={f.props.reference}
                source={f.props.source}
                value={filterValues[f.props.source]}
              />
            ))
          }
          {filters.filter(f => (
            Object.prototype.hasOwnProperty.call(displayedFilters, f.props.source) &&
            !(
              f.props.alwaysOn ||
              Object.prototype.hasOwnProperty.call(filterValues, f.props.source)
            )
          )).map((f, i) => (
            <div className={styles.filterInput} key={i}>
              {cloneElement(f, {clearIcon: null})}
              <CloseIcon onClick={() => hideFilter(f.props.source)}/>
            </div>
          ))}
          {canShowFiltersButton && (
            <Button
              className={styles.showInactiveFiltersButton}
              color="primary"
              disabled={!(advancedFiltersConfig || inactiveFilters.length)}
              onClick={() => setIsFilterOptionPopperOpen(true)}
              ref={anchorElementRef}
              startIcon={<FilterIcon />}
              variant="outlined"
            >
              {translate('actions.showInactiveFilters')}
            </Button>
          )}
          {!!restoredDisplayedFilters?.length && (
            <Button
              className={styles.showInactiveFiltersButton}
              variant="outlined"
              onClick={() => {
                setIsFilterOptionPopperOpen(false)
                setIsAdvancedFilterOptionPopperOpen(v => !v)
              }}
              endIcon={(
                <Box
                  className={styles.appliedFiltersButtonEndIcon}
                  onClick={e => {
                    e.stopPropagation()
                    deleteAllAppliedAdvancedFilters()
                  }}
                >
                  <CloseIcon />
                </Box>
              )}
            >
              {translate(
                'advancedFiltering.appliedFiltersButton',
                {count: restoredDisplayedFilters.flat().length}
              )}
            </Button>
          )}
        </SimpleForm>
        {shouldAlignActionButtonsWithFilterForm && (
          <ExtendedListActions
            shouldAlignActionButtonsWithFilterForm={
              shouldAlignActionButtonsWithFilterForm
            }
            actionButtons={actionButtons}
            onCreateButtonClick={onCreateButtonClick}
          />
        )}
      </EditContextProvider>
      <Popper
        anchorEl={anchorElementRef.current}
        className={styles.addFilterPopper}
        open={isFilterOptionPopperOpen}
        placement="bottom-start"
      >
        <ClickAwayListener onClickAway={() => setIsFilterOptionPopperOpen(false)}>
          <Paper className={styles.addFilterPaper} elevation={5}>
            {inactiveFilters.map((filter, key) => (
              <Button
                className={styles.addFilterOption}
                fullWidth
                key={key}
                onClick={() => {
                  trackEvent({
                    action: 'SHOW_FILTER',
                    category: 'FILTER',
                    source: resource?.toUpperCase(),
                    value: filter.props.label ?? getFieldLabel(filter.props.source),
                  })
                  showFilter(filter.props.source, undefined)
                  setIsFilterOptionPopperOpen(false)
                }}
              >
                {filter.props.label ?? getFieldLabel(filter.props.source)}
              </Button>
            ))}
            <Divider />
            {isAdvancedFilteringEnabled && (
              <Button
                className={styles.addAdvancedFilterButton}
                fullWidth
                onClick={() => {
                  setIsFilterOptionPopperOpen(false)
                  setIsAdvancedFilterOptionPopperOpen(true)
                }}
              >
                <AddIcon size="small" />
                {translate('advancedFiltering.openButtonTitle')}
              </Button>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
      <Popper
        anchorEl={anchorElementRef.current}
        className={styles.addFilterPopper}
        open={isDesktop && isAdvancedFilterOptionPopperOpen}
        placement="bottom-start"
      >
        <Paper className={styles.addFilterPaper} elevation={5}>
          <SimpleForm className={styles.advancedFiltersForm} toolbar={false}>
            {isAdvancedFilteringEnabled && (
              <AdvancedFiltersFormFields
                advancedFiltersConfig={advancedFiltersConfig}
                onDeleteAllAppliedAdvancedFilters={deleteAllAppliedAdvancedFilters}
                onClose={() => setIsAdvancedFilterOptionPopperOpen(false)}
              />
            )}
          </SimpleForm>
        </Paper>
      </Popper>
      <ExtendedDialog
        className={styles.addFilterDialog}
        fullScreen={true}
        onClose={() => setIsAdvancedFilterOptionPopperOpen(false)}
        open={!isDesktop && isAdvancedFilterOptionPopperOpen}
      >
        <DialogTitle>{translate('advancedFiltering.title')}</DialogTitle>
        <DialogCloseButton onClick={() => setIsAdvancedFilterOptionPopperOpen(false)} />
        <DialogContent>
          <Paper className={styles.addFilterPaper}>
            <SimpleForm className={styles.advancedFiltersForm} toolbar={false}>
              {advancedFiltersConfig && (
                <AdvancedFiltersFormFields
                  advancedFiltersConfig={advancedFiltersConfig}
                  onDeleteAllAppliedAdvancedFilters={deleteAllAppliedAdvancedFilters}
                  onClose={() => setIsAdvancedFilterOptionPopperOpen(false)}
                />
              )}
            </SimpleForm>
          </Paper>
        </DialogContent>
      </ExtendedDialog>
    </Box>
  )
}

const AdvancedFiltersFormFields: FC<AdvancedFiltersFormFieldsProps> = ({
  advancedFiltersConfig: filtersConfig, onClose, onDeleteAllAppliedAdvancedFilters,
}) => {
  const translate = useTranslate()
  const {classes: styles} = useStyles({})
  const {
    filterValues, setFilters,
  }: any & {filterValues: NestedHasuraFilter} = useListContext()
  const {setValue} = useFormContext()
  const emptyFilterLine: AdvancedDisplayedFilter = {
    operator: null, source: null, value: null,
  }
  const restoredDisplayedFilters = useAdvancedDisplayedFiltersFromHasuraFilterValues()
  const displayedFilters = useWatch<AdvancedDisplayedFilter[][]>({
    defaultValue: restoredDisplayedFilters?.length ?
      restoredDisplayedFilters : [[emptyFilterLine]],
    // @ts-ignore: error TS2769: No overload matches this call.
    name: 'displayedFilters',
  }) as AdvancedDisplayedFilter[][]
  return (
    <Box className={styles.advancedFiltersForm} width="100%">
      <Box
        alignItems="center"
        className={styles.advancedFiltersFormTitleBox}
        display="flex"
        justifyContent="space-between"
        width="100%"
      >
        <Typography variant="subtitle1">
          {translate('advancedFiltering.title')}
        </Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <div className={styles.advancedFiltersList}>
        {displayedFilters.map((orFilterGroup, orIndex) => (
          <Fragment key={orIndex}>
            {!!orIndex && (
              <Typography className={styles.orSeparator}>
                {translate('advancedFiltering.logic.or').toUpperCase()}
              </Typography>
            )}
            <div className={styles.andFiltersWrapper}>
              {orFilterGroup.map((f, andIndex) => {
                const selectSourceFilterConfig = filtersConfig[
                  displayedFilters[orIndex]?.[andIndex]?.source as string
                ]
                return (
                  <Box
                    className={styles.advancedFilterLineContainer}
                    key={andIndex}
                    width="100%"
                  >
                    <Box width="100%" className={styles.advancedFilterLine}>
                      <SelectInput
                        choices={Object.keys(filtersConfig).map(source => ({
                          name: filtersConfig[source].label, value: source,
                        }))}
                        defaultValue={f.source}
                        label={translate('advancedFiltering.displayedFilters.source')}
                        onChange={() => {
                          setValue(
                            `displayedFilters.${orIndex}.${andIndex}.operator`,
                            null
                          )
                          setValue(`displayedFilters.${orIndex}.${andIndex}.value`, null)
                        }}
                        optionText="name"
                        optionValue="value"
                        source={`displayedFilters.${orIndex}.${andIndex}.source`}
                        validate={required()}
                      />
                      <SelectInput
                        choices={
                          selectSourceFilterConfig?.operators.map(op => ({
                            name: op.label ??
                              translate(`advancedFiltering.operators.${op.value}`),
                            value: op.value,
                          }))
                        }
                        defaultValue={f.operator}
                        label={translate('advancedFiltering.displayedFilters.operator')}
                        optionText="name"
                        optionValue="value"
                        source={`displayedFilters.${orIndex}.${andIndex}.operator`}
                        validate={required()}
                      />
                      {selectSourceFilterConfig?.input({
                        defaultValue: displayedFilters[orIndex]?.[andIndex].value,
                        label: selectSourceFilterConfig?.label,
                        source: `displayedFilters.${orIndex}.${andIndex}.value`,
                      }) ?? (
                        <SelectInput
                          label={
                            selectSourceFilterConfig ?
                              selectSourceFilterConfig.label :
                              translate('advancedFiltering.displayedFilters.value')
                          }
                          optionText="name"
                          optionValue="value"
                          source={`displayedFilters.${orIndex}.${andIndex}.value`}
                        />
                      )}
                    </Box>
                    <IconButton
                      className={styles.closeIconButton}
                      size="small"
                      disabled={
                        (displayedFilters.length === 1) &&
                        (displayedFilters[orIndex].length === 1)
                      }
                      onClick={() => {
                        const filters = [...displayedFilters]
                        filters[orIndex].splice(andIndex, 1)
                        if (filters.length > 1 && !filters[orIndex].length) {
                          filters.splice(orIndex, 1)
                        }
                        setValue('displayedFilters', filters)
                      }}
                    >
                      <CloseIcon size="small" />
                    </IconButton>
                  </Box>
                )
              })}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  const filters = [...displayedFilters]
                  filters[orIndex].push(emptyFilterLine)
                  setValue('displayedFilters', filters)
                }}
              >
                {translate('advancedFiltering.logic.and')}
              </Button>
            </div>
          </Fragment>
        ))}
        <Button
          className={styles.addFilterBlockButton}
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            setValue('displayedFilters', [...displayedFilters, [emptyFilterLine]])
          }}
        >
          {translate('advancedFiltering.logic.or')}
        </Button>
      </div>
      <Box className={styles.advancedFiltersFormFooter}>
        <Button
          onClick={() => {
            const validateDisplayedFilter = (filter: AdvancedDisplayedFilter) =>
              !!filter.source && !!filter.operator
            const validDisplayedFilters = displayedFilters
              .map(filterLines => filterLines.filter(f => validateDisplayedFilter(f)))
              .filter(filterLines => !!filterLines.length)
            const filters = nestedFieldFiltersFromAdvancedDisplayedFilters(
              validDisplayedFilters
            )
            if (filters.length) {
              const booleanExpression: HasuraBooleanExpression = filters.length === 1 ?
                filters[0] : // Single filter group - use directly without extra wrapping
                {_or: filters} // Multiple filter groups - wrap in `_or`
              setFilters({
                ...filterValues,
                _and: {
                  format: 'hasura-raw-query',
                  value: booleanExpression,
                },
              })
            }
            onClose()
          }}
          variant="contained"
        >
          {useTranslate()('actions.apply')}
        </Button>
        <Button
          onClick={onDeleteAllAppliedAdvancedFilters}
          variant="contained"
          color="error"
        >
          {useTranslate()('actions.deleteAll')}
        </Button>
      </Box>
    </Box>
  )
}

const FilledFilterChip = ({
  childProps,
  choices,
  hideFilter,
  label,
  optionText,
  optionValue,
  reference,
  source,
  value,
}) => {
  const styles = useStyles({hasFilters: true}).classes
  const {resetField} = useFormContext()
  const filterLabel = reference ?
    <ReferenceFilterLabel
      {...{label, optionText: childProps.optionText, reference, value}}
    /> :
    <FilterLabel {...{choices, label, optionText, optionValue, value}}/>
  return (
    <Chip
      className={styles.filterChip}
      deleteIcon={<CloseIcon />}
      label={filterLabel}
      onDelete={() => {
        resetField(source)
        hideFilter(source)
      }}
      variant="outlined"
    />
  )
}

const FilterLabel = ({choices, label, optionText, optionValue, value}) => {
  const {getChoiceText} = useChoices({optionText, optionValue})
  return (
    <>
      {label}: {' '}
      {optionText ? getChoiceText(choices?.find(c => c[optionValue] === value)) : value}
    </>
  )
}

const ReferenceFilterLabel = ({label, optionText, reference, value}) => {
  const {isPending, referenceRecord} = useReference({id: value, reference})
  const {getChoiceText} = useChoices({optionText})
  return !isPending && referenceRecord ?
    <>{label}: {getChoiceText(referenceRecord)}</> :
    null
}

const ExtendedListHeader: FC<ExtendedListHeaderProps> = ({
  description, hasFilters, shouldAlignActionButtonsWithFilterForm, title, ...props
}) => {
  const styles = useStyles({hasFilters}).classes
  return (
    <div className={styles.extendedListActions}>
      <DisplayBox desktop mobile={false}>
        <Box
          alignSelf="center"
          display="flex"
          flexWrap="wrap"
          justifyContent="space-between"
          width="100%"
        >
          {title !== false && <ResourceTitle description={description} title={title} />}
          {!shouldAlignActionButtonsWithFilterForm && (
            <ExtendedListActions
              shouldAlignActionButtonsWithFilterForm={
                shouldAlignActionButtonsWithFilterForm
              }
              {...{...props}}
            />
          )}
        </Box>
      </DisplayBox>
    </div>
  )
}

const ExtendedListActions: FC<ExtendedListActionsProps> = ({
  actionButtons, onCreateButtonClick, shouldAlignActionButtonsWithFilterForm,
}) => {
  const translate = useTranslate()
  const resource = useResourceContext()
  return (
    <Box
      alignItems="center"
      display="flex"
      flexWrap="wrap"
      justifyContent="end"
      width={shouldAlignActionButtonsWithFilterForm ? '100%' : undefined}
    >
      {actionButtons}
      {useHasPermission('create', resource!) && (
        <CreateButton
          label={translate(`resources.${resource}.action.create`)}
          onClick={onCreateButtonClick}
          size="medium"
          variant="contained"
        />
      )}
    </Box>
  )
}

const useGetFieldLabel = () => {
  const translate = useTranslate()
  const resource = useResourceContext()
  return useCallback(
    (source: string) => translate(`resources.${resource}.fields.${source}`),
    [resource, translate],
  )
}

/**
 * Given the current filter values, returns the displayed filters as an array of
 * arrays of `AdvancedDisplayedFilter` objects.
 *
 * @returns {AdvancedDisplayedFilter[][]} The displayed filters
 */
const useAdvancedDisplayedFiltersFromHasuraFilterValues = () => {
  const {filterValues}: {filterValues: NestedHasuraFilter} = useListContext()
  return useMemo<AdvancedDisplayedFilter[][]>(
    () => {
      if (!filterValues._and?.value) return []
      const parseBooleanExpression = (
        exp: HasuraBooleanExpression
      ): AdvancedDisplayedFilter[][] => {
        if ('_or' in exp) {
          // Handle _or: parse each item in the _or array
          return exp._or!.map(item => {
            if ('_and' in item) {
              // Handle nested _and within _or
              return (item._and as Array<HasuraBooleanExpression>).flatMap(andItem =>
                advancedDisplayedFiltersFromNestedFilter(andItem as NestedFieldFilter)
              )
            }
            if ('_or' in item) {
              // Handle nested _or within _or (flatten)
              return parseBooleanExpression(item).flat()
            }
            // Handle direct filter
            return advancedDisplayedFiltersFromNestedFilter(item as NestedFieldFilter)
          })
        }
        if ('_and' in exp) {
          // Handle _and: all conditions should be in the same group
          return [exp._and!.flatMap(item =>
            advancedDisplayedFiltersFromNestedFilter(item as NestedFieldFilter)
          )]
        }
        // Handle direct filter
        return [advancedDisplayedFiltersFromNestedFilter(exp as NestedFieldFilter)]
      }
      return parseBooleanExpression(filterValues._and.value)
    }, [filterValues]
  )
}

const nestedFieldFiltersFromAdvancedDisplayedFilters = (
  filters: AdvancedDisplayedFilter[][] | undefined
): (NestedFieldFilter | HasuraBooleanExpression)[] => {
  if (!filters?.length) return []
  return filters.map(filterLines => {
    const nestedFilters = filterLines.map(filter => {
      const {operator, source, value} = filter
      if (source && operator && (value !== null || ['_eq', '_neq'].includes(operator))) {
        return nestedFieldFilterFromAdvancedDisplayedFilter(
          filter as RequiredAdvancedDisplayedFilter
        )
      }
      return null
    }).filter(Boolean) as NestedFieldFilter[]
    // If there's only one filter in the group, return it directly
    // If there are multiple filters, wrap them in an _and condition
    if (nestedFilters.length === 1) return nestedFilters[0]
    if (nestedFilters.length > 1) return {_and: nestedFilters}
    // This case shouldn't happen if validation is working correctly - we must
    // always have at least 1 nestedFilter.
    return {}
  }).filter(filter => Object.keys(filter).length > 0)
}

// Eventually renaming this to: toHasuraNestedFilter
const nestedFieldFilterFromAdvancedDisplayedFilter = (
  {operator, source, value}: RequiredAdvancedDisplayedFilter
): NestedFieldFilter => {
  const keys = source.split('#')
  let _operator = operator
  let _value = value
  // Convert _eq and _neq to _is_null and with value true or false.
  // todo: This wont be needed anymore if one day we support _is_null in the UI.
  if (value === null) {
    _operator = '_is_null'
    _value = operator === '_eq'
  }
  if (operator === '_ilike') _value = `%${_value}%`
  const finalValue = {[_operator]: _value} as unknown as NestedFieldFilter
  return keys.reduceRight<NestedFieldFilter>((acc, key) => ({[key]: acc}), finalValue)
}

const advancedDisplayedFiltersFromNestedFilter = (
  hasuraNestedFieldFilter: NestedFieldFilter,
  sourceParts: string[] = [],
): AdvancedDisplayedFilter[] => {
  const flattenedFilters: AdvancedDisplayedFilter[] = []
  Object.entries(hasuraNestedFieldFilter)
    // @ts-ignore
    .forEach(([fieldKey, subFilter]: [string, NestedFieldFilter]) => {
      if (typeof subFilter === 'object' && subFilter !== null) {
        const subFilterKeys = Object.keys(subFilter)
        if (subFilterKeys.length === 1 && subFilterKeys[0].startsWith('_')) {
          let operator = subFilterKeys[0]
          let value = subFilter[operator]
          // Convert back _is_null to _eq and _neq with value true or false.
          // todo: This wont be needed anymore if one day we support _is_null in the UI.
          if (operator === '_is_null') {
            operator = value ? '_eq' : '_neq'
            value = null
          }
          if (operator === '_ilike') {
            // Strip a single leading and trailing % only when both are present.
            // Example: "%foo%" -> "foo".
            value = value.replace(/^%(.+)%$/, '$1')
          }
          flattenedFilters.push({
            operator,
            source: [...sourceParts, fieldKey].join('#'),
            value,
          })
        }
        else {
          const nested = advancedDisplayedFiltersFromNestedFilter(
            subFilter,
            [...sourceParts, fieldKey],
          )
          flattenedFilters.push(...nested)
        }
      }
    })
  return flattenedFilters
}

const useStyles = makeStyles<MakeStylesOptions, 'closeIconButton'>()(
  (theme, {hasFilters}, classes
  ) => ({
    addAdvancedFilterButton: {
      alignItems: 'center',
      color: theme.palette.info.main,
      gap: theme.remSpacing(1),
      justifyContent: 'start',
      margin: 0,
      padding: theme.remSpacing(2),
    },
    addFilterBlockButton: {
      marginTop: theme.remSpacing(1.5),
    },
    addFilterDialog: {
      '& .MuiDialogContent-root': {
        padding: 0,
      },
    },
    addFilterOption: {
      justifyContent: 'start',
      margin: 0,
      padding: `${theme.remSpacing(1)} ${theme.remSpacing(2)}`,
    },
    addFilterPaper: {
      [theme.breakpoints.up('lg')]: {
        marginTop: theme.remSpacing(1),
        minWidth: `max(50vw, ${theme.remSpacing(24)})`,
      },
    },
    addFilterPopper: {
      zIndex: 100,
    },
    advancedFilterLine: {
      display: 'flex',
      gap: theme.remSpacing(1),
      [theme.breakpoints.down('lg')]: {
        alignItems: 'center',
        flexDirection: 'column',
        gap: 0,
      },
    },
    advancedFilterLineContainer: {
      display: 'flex',
      [theme.breakpoints.down('lg')]: {
        '&:not(:last-of-type)': {
          borderBottom: `1px solid ${theme.palette.disabled.main}`,
          marginBottom: theme.remSpacing(2),
          paddingBottom: theme.remSpacing(2),
        },
        flexDirection: 'column-reverse',
        gap: 0,
      },
      [`& .${classes.closeIconButton}`]: {
        alignSelf: 'end',
        [theme.breakpoints.up('lg')]: {
          alignSelf: 'center',
          height: '40px',
          width: '40px',
        },
      },
    },
    advancedFiltersForm: {
      padding: theme.remSpacing(1.5),
      [theme.breakpoints.down('lg')]: {
        padding: theme.remSpacing(1),
      },
      paddingBottom: 0,
    },
    advancedFiltersFormFooter: {
      display: 'flex',
      gap: theme.remSpacing(1.5),
      justifyContent: 'end',
      marginTop: theme.remSpacing(1.5),
      width: '100%',
    },
    advancedFiltersFormTitleBox: {
      [theme.breakpoints.down('lg')]: {
        display: 'none',
      },
    },
    advancedFiltersList: {
      [theme.breakpoints.up('lg')]: {
        maxHeight: '25vh',
        overflowY: 'auto',
      },
    },
    andFiltersWrapper: {
      border: `1px solid ${theme.palette.disabled.main}`,
      padding: theme.remSpacing(2),
    },
    appliedFiltersButtonEndIcon: {
      '&:hover': {
        backgroundColor: theme.palette.info.dark,
      },
      borderRadius: '50%',
      padding: '2px',
    },
    closeIconButton: {},
    extendedListActions: {
      [theme.breakpoints.up('lg')]: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.remSpacing(2),
        justifyContent: 'space-between',
        width: '100%',
      },
    },
    filterChip: {
      [theme.breakpoints.up('md')]: {
        marginTop: theme.remSpacing(1),
      },
      '& .MuiChip-deleteIcon': {
        color: theme.palette.info.main,
      },
      borderColor: theme.palette.info.main,
      borderRadius: theme.remSpacing(1),
      color: theme.palette.info.main,
      height: theme.remSpacing(7.5),
    },
    filterForm: {
      '& .MuiFilledInput-input': {
        width: theme.remSpacing(25),
      },
      '& .MuiStack-root': {
        '& > form': {
          width: 'initial',
          [theme.breakpoints.down('lg')]: {
            width: '100%',
          },
        },
        alignItems: 'start',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: theme.remSpacing(2),
      },
    },
    filterInput: {
      '& > svg': {
        '&:hover': {
          backgroundColor: theme.palette.disabled.main,
        },
        background: theme.palette.background.paper,
        borderRadius: '50%',
        color: theme.palette.text.secondary,
        cursor: 'pointer',
        padding: theme.remSpacing(0.5),
        position: 'absolute',
        right: theme.remSpacing(0.5),
        top: 'calc(50% - 20px)',
        transition: 'all .3s ease',
      },
      minWidth: '20%',
      position: 'relative',
    },
    orSeparator: {
      marginBottom: theme.remSpacing(1.5),
      marginLeft: theme.remSpacing(1),
      marginTop: theme.remSpacing(1.5),
    },
    root: {
      [
      '& .MuiToolbar-root:not(.MuiTablePagination-toolbar)' +
      ':not([data-test="bulk-actions-toolbar"])'
      ]: {
        flexDirection: 'column-reverse',
        marginBottom: theme.remSpacing(3),
        marginLeft: theme.remSpacing(5),
        [theme.breakpoints.down('md')]: {
          marginLeft: theme.remSpacing(4),
        },
      },
      ...(hasFilters && {
        '& .MuiTextField-root': {
          [theme.breakpoints.down('sm')]: {
            margin: 0,
            padding: 0,
          },
          '& .MuiInputBase-input::placeholder': {
            color: theme.palette.text.secondary,
            opacity: 1,
          },
        },
        '& form': {
          [theme.breakpoints.down('sm')]: {
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          },
          '& .filter-field': {
            [theme.breakpoints.down('sm')]: {
              // Select the element that adds an empty space to the right
              '& > :nth-of-type(2)': {
                display: 'none',
              },
              alignSelf: 'center',
              margin: 0,
              padding: 0,
              width: '100%',
            },
          },
          width: '100%',
        },
      }),
    },
    selectAllPagesField: {
      [theme.breakpoints.up('lg')]: {
        marginLeft: theme.remSpacing(3),
      },
      marginLeft: theme.remSpacing(2),
    },
    showInactiveFiltersButton: {
      [theme.breakpoints.up('md')]: {
        marginTop: theme.remSpacing(1),
      },
      height: theme.remSpacing(7.5),
      margin: 0,
    },
  }))

type HasuraOperator = '_eq' | '_ilike' | '_neq'
type CustomFilterOperatorProps = Omit<FilterOperatorProps, 'operator'> & {
  defaultValue?: any
}
type CustomFilterDefinition = Omit<FilterDefinition, 'input' |'operators'> & {
  input: (props: CustomFilterOperatorProps) => ReactElement
  operators: (
    Omit<FilterOperator, 'label' | 'value'> & {
      label?: string
      value: HasuraOperator
    }
  )[]
}
type CustomFilterConfig = Record<string, CustomFilterDefinition>

// Terminal filter condition (e.g. _eq: value)
type HasuraCondition = {
  [op in HasuraOperator]?: unknown
};

/*
  A recursively nested object that ends with a HasuraCondition.
  "NestedFieldFilter" represents this:
  {
    merchantCustomerUsers: {
      merchantCustomerUserTags: {
        tagId: {_eq: "tag-id"}
      }
    }
  }
*/
type NestedFieldFilter =
  | {[fieldName: string]: NestedHasuraFilter} // Recursive nesting of fields
  | HasuraCondition; // Terminal filter condition (e.g. _eq: value)

/*
  The top-level structure with "_and" and the "hasura-raw-query" wrapper
  "NestedHasuraFilter" represents this:
  _and: {
    format: 'hasura-raw-query',
    value: {
      _or: [
        {
          _and: [
            {firstName: {_ilike: "e%"}},
            {firstName: {_nilike: "%n"}}
          ]
        },
        {
          _and: [
            {merchantCustomerUserTags: {tagId: {_eq: "tag-id-1"}}},
            {merchantCustomerUserTags: {tagId: {_eq: "tag-id-2"}}}
          ]
        }
      ]
    },
  },

  TODO: In the future, the shape of "NestedHasuraFilter" to the following one. It slightly
  improves performanaces.
  _and: {
    format: 'hasura-raw-query',
    value: {
      _or: [
        {
          firstName: {
            _and: [
              {_ilike: "e%"},
              {_nilike: "%n"}
            ]
          },
          merchantCustomerUsers: {
            _and: [
              {merchantCustomerUserTags: {tagId: {_eq: "tag-id-1"}}},
              {merchantCustomerUserTags: {tagId: {_eq: "tag-id-2"}}},
            ]
          }
        },
        {
          merchantCustomerUsers: {
            _and: [
              {merchantCustomerUserTags: {tagId: {_eq: "tag-id-3"}}},
              {merchantCustomerUserTags: {tagId: {_eq: "tag-id-4"}}},
            ]
          }
        }
      ]
    },
  },
}
*/
type NestedHasuraFilter = {
  _and: {
    format: 'hasura-raw-query'
    value: HasuraBooleanExpression
  }
}

// Flexible nested structure: Flexible condition that supports nested _and/_or operations
// that represents hasura's boolean expression schema.
// Could also be named "HasuraWhereCondition" or "HasuraWhereExpression".
type HasuraBooleanExpression =
  // TypeScript now knows that if _or exists, _and definitely doesn't (and vice versa)
  | {_or: (NestedFieldFilter | HasuraBooleanExpression)[], _and?: never}
  | {_and: (NestedFieldFilter | HasuraBooleanExpression)[], _or?: never}
  | (NestedFieldFilter & {_or?: never, _and?: never})

interface ExtendedListProps extends ListProps {
  advancedFiltersConfig?: CustomFilterConfig
  actionButtons?: ReactNode
  canSelectAllPages?: boolean,
  children: ReactElement,
  description?: ReactNode,
  filters?: FiltersFormProps['filters'],
  hasCreate?: boolean,
  onCreateButtonClick?: MouseEventHandler<HTMLAnchorElement>
  onHasDisplayedEmptyFiltersChange?: (hasDisplayedEmptyFilters: boolean) => void
  shouldAlignActionButtonsWithFilterForm?: boolean
}

interface MakeStylesOptions {
  hasFilters?: boolean,
}

interface ExtendedListHeaderProps extends
  Pick<
    ExtendedListProps,
    'actionButtons' |
    'description' |
    'onCreateButtonClick' |
    'title' |
    'shouldAlignActionButtonsWithFilterForm'
  >{
  hasFilters: boolean
}

interface FiltersFormProps extends
  Pick<
    ExtendedListProps,
    'actionButtons' |
    'advancedFiltersConfig' |
    'onCreateButtonClick' |
    'shouldAlignActionButtonsWithFilterForm'
  >{
  filters: [FilterElement, ...FilterElement[]]
  onHasDisplayedEmptyFiltersChange?: (hasDisplayedEmptyFilters: boolean) => void
}

interface AdvancedFiltersFormFieldsProps {
  advancedFiltersConfig: CustomFilterConfig
  onDeleteAllAppliedAdvancedFilters: VoidFunction
  onClose: VoidFunction
}

interface AdvancedDisplayedFilter {
  source: string | null
  operator: string | null
  value: string | boolean | null
}

type RequiredAdvancedDisplayedFilter = {
  [K in keyof AdvancedDisplayedFilter]: NonNullable<AdvancedDisplayedFilter[K]>
}

type FilterElement = ReactElement<{
  [key:string]: any
  alwaysOn?: boolean
  label: string
  source: string
}>

type ExtendedListActionsProps =
  Pick<
    ExtendedListProps,
    'actionButtons' | 'onCreateButtonClick' | 'shouldAlignActionButtonsWithFilterForm'
  >

export default ExtendedList
export type {CustomFilterConfig}
